import { useGtm } from '@gtm-support/vue-gtm';
import { getUserInfo } from '@/api/apiService';
import store from '@/vuex';

/*
 ** GA4 推送的參數命名必須避開 GA4 的預設參數
 ** 參考資料：https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference?utm_source=chatgpt.com&hl=zh-tw&client_type=firebase
 */
export function useGtmEventManager() {
  // gtm事件追蹤購票流程行為
  const UserProgressStage = Object.freeze({
    NO_ACT: { order: 0, value: 'no_act' }, // 沒有操作
    CLICK_PLAN: { order: 1, value: 'click_plan' }, // 點擊方案
    SELECT_TIME: { order: 2, value: 'select_time' }, // 選擇時間
    SELECT_QUANTITY: { order: 3, value: 'select_quantity' }, // 選擇張數
    ADD_TO_CART: { order: 4, value: 'add_to_cart' }, // 加入購物車
    BUY: { order: 5, value: 'buy' }, // 直接購買
    CART_CONFIRMATION: { order: 6, value: 'cart_confirmation' }, // 購物車確認
    ORDER_CONFIRMATION: { order: 7, value: 'order_confirmation' }, // 訂單確認
    PURCHASE_SUCCESS: { order: 8, value: 'purchase_success' }, // 購買成功
    PURCHASE_FAILED: { order: 9, value: 'purchase_failed' }, // 購買失敗
  });

  const pushGtmEvent = (eventName, eventData) => {
    const gtm = useGtm();
    if (gtm) {
      gtm.push({
        event: eventName,
        ...eventData,
      });
    } else {
      console.warn('GTM 實例尚未初始化');
    }
  };

  // 嘗試取得使用者UID
  const getUserId = async () => {
    if (!store.state.loginMethod) return 'noLogin';
    if (!store.state.userId) await getUserInfo(store.state.jwt, true);
    return store.state.userId;
  };

  // 取得使用者平台
  const getPlatformType = () => {
    const ua = navigator.userAgent;

    if (/android/i.test(ua)) {
      return 'android';
    }
    if (/iPad|iPhone|iPod/.test(ua)) {
      return 'ios';
    }
    return 'web';
  };

  // 官網首頁 成功載入事件
  const homepageInitGtmEvent = async () => {
    const userId = await getUserId();
    const currentLanguage = store.state.currentLanguage;
    pushGtmEvent('homepage_init', {
      user_uid: userId,
      current_language: currentLanguage,
      device_type: getPlatformType(),
    });
  };

  // 官網首頁 離開頁面或是切換頁面時觸發事件
  const homepageActGtmEvent = async (staySeconds) => {
    const userId = await getUserId();
    pushGtmEvent('homepage_act', {
      user_uid: userId,
      stay_seconds: staySeconds,
    });
  };

  // 官網首頁 點擊 BANNER 事件
  const bannerClickGtmEvent = async (bannerName) => {
    const userId = await getUserId();
    pushGtmEvent('homepage_banner_click', {
      user_uid: userId,
      banner_name: bannerName,
    });
  };

  // 售票頁 成功載入售票頁時觸發事件
  const productInfoClickGtmEvent = async (productInfoName) => {
    const userId = await getUserId();
    const langHistoryStr = localStorage.getItem('langHistory');
    const fromUrl = JSON.parse(langHistoryStr || '[]')[0] || null;
    const currentLanguage = store.state.currentLanguage;
    pushGtmEvent('ticket_page_enter', {
      user_uid: userId,
      product_info_name: productInfoName,
      source_url: fromUrl,
      current_language: currentLanguage,
      device_type: getPlatformType(),
    });
  };

  // 售票頁 離開頁面或是切換頁面時觸發事件
  const productInfoActGtmEvent = async (productInfoName, staySeconds) => {
    const userId = await getUserId();
    const currentLanguage = store.state.currentLanguage;
    pushGtmEvent('ticket_act', {
      user_uid: userId,
      product_info_name: productInfoName,
      stay_seconds: staySeconds,
      current_language: currentLanguage,
      device_type: getPlatformType(),
    });
  };

  // 售票頁 紀錄玩家流程事件 有進展任何流程時觸發
  const productInfoProgressGtmEvent = async (productInfoName, userProgressStage) => {
    const userId = await getUserId();
    const currentLanguage = store.state.currentLanguage;
    pushGtmEvent('ticket_progress', {
      user_uid: userId,
      product_info_name: productInfoName,
      user_progress_stage: userProgressStage,
      current_language: currentLanguage,
      device_type: getPlatformType(),
    });
  };

  // 購物車頁面 確定購買的按鈕按下時觸發
  const cartConfirmationGtmEvent = async (productInfoName, userProgressStage) => {
    const userId = await getUserId();
    const currentLanguage = store.state.currentLanguage;
    pushGtmEvent('cart_act', {
      user_uid: userId,
      product_info_name: productInfoName,
      user_progress_stage: userProgressStage,
      current_language: currentLanguage,
      device_type: getPlatformType(),
    });
  };

  // 付款後頁面 進入付款頁面後觸發
  const purchaseInitGtmEvent = async (productInfoName, userProgressStage, orderId) => {
    const userId = await getUserId();
    const currentLanguage = store.state.currentLanguage;
    pushGtmEvent('purchase_act', {
      user_uid: userId,
      product_info_name: productInfoName,
      user_progress_stage: userProgressStage,
      current_language: currentLanguage,
      device_type: getPlatformType(),
      order_id: orderId,
    });
  };

  return {
    UserProgressStage,
    homepageInitGtmEvent,
    homepageActGtmEvent,
    bannerClickGtmEvent,
    productInfoClickGtmEvent,
    productInfoActGtmEvent,
    productInfoProgressGtmEvent,
    cartConfirmationGtmEvent,
    purchaseInitGtmEvent,
  };
}
